import { useMemo, useState } from 'react'
import ExportBar from './ExportBar'
import ReportControls, { type ReportFilter } from './ReportControls'
import ReportTable, { type ReportTableColumn } from './ReportTable'
import { getTeamLookups } from './coverageReportStore'
import { rosterExportTable } from './peopleReportExport'
import { fullDate, todayIso } from './reportDates'
import { nextSort, sortRows, type ReportSort, type SortValue } from './reportSort'
import { ROSTER_STATUS_LABEL, buildRoster, filterRoster, type RosterFilters, type RosterRow, type RosterStatus } from './rosterFigures'
import { getRosterRecords } from './seniorityRosterStore'
import { supervisorLabel } from './supervisorLabel'
import { useLoaded } from './useLoaded'

/**
 * docs/product/reports.md's seniority roster: everyone in scope in seniority
 * order, with team, title, shift, qualifications, and whether they are active
 * or on detail, as of today.
 */
const COLUMNS: ReportTableColumn<RosterRow>[] = [
  { id: 'rank', label: 'Seniority', cell: (row) => row.rank },
  { id: 'employee', label: 'Employee', cell: (row) => row.employee },
  { id: 'identifier', label: 'Identifier', cell: (row) => row.identifier },
  { id: 'seniorityDate', label: 'Seniority date', cell: (row) => fullDate(row.seniorityDate) },
  { id: 'team', label: 'Team', cell: (row) => row.team },
  { id: 'title', label: 'Title', cell: (row) => row.title },
  { id: 'shift', label: 'Shift', cell: (row) => row.shift },
  { id: 'qualifications', label: 'Qualifications', cell: (row) => (row.qualifications.length ? row.qualifications.join(', ') : 'None') },
  { id: 'status', label: 'Status', cell: (row) => ROSTER_STATUS_LABEL[row.status] },
]

const SORT_VALUE: Record<string, (row: RosterRow) => SortValue> = {
  rank: (row) => row.rank,
  employee: (row) => row.employee,
  identifier: (row) => row.identifier,
  seniorityDate: (row) => row.seniorityDate,
  team: (row) => row.team,
  title: (row) => row.title,
  shift: (row) => row.shift,
  qualifications: (row) => row.qualifications.join(', ') || null,
  status: (row) => row.status,
}

function Tile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-card border border-line bg-canvas p-4">
      <p className="text-caption font-bold tracking-wide text-ink-subtle uppercase">{label}</p>
      <p className="mt-1 text-h2 font-extrabold [font-variant-numeric:tabular-nums]">{value}</p>
    </div>
  )
}

export default function SeniorityRoster() {
  const [filters, setFilters] = useState<RosterFilters>({})
  const [sort, setSort] = useState<ReportSort>({ column: 'rank', direction: 'asc' })
  const { data, loading, failed } = useLoaded(
    async () => {
      const [records, lookups] = await Promise.all([getRosterRecords(), getTeamLookups()])
      return { records, lookups }
    },
    { records: [], lookups: { teams: new Map<string, string>(), supervisors: new Map<string, string>() } },
  )
  const { lookups } = data

  const roster = useMemo(() => buildRoster(data.records, lookups.supervisors), [data.records, lookups])
  const filtered = useMemo(() => filterRoster(roster, filters), [roster, filters])
  const rows = useMemo(() => sortRows(filtered, SORT_VALUE[sort.column], sort.direction), [filtered, sort])

  const reportFilters: ReportFilter[] = [
    {
      id: 'team',
      label: 'Team',
      allLabel: 'All teams',
      value: filters.teamId ?? '',
      options: Array.from(new Set(roster.map((row) => row.teamId))).map((id) => ({ value: id, label: lookups.teams.get(id) ?? 'Unknown team' })),
      onChange: (value) => setFilters((current) => ({ ...current, teamId: value || undefined })),
    },
    {
      id: 'supervisor',
      label: 'Supervisor',
      allLabel: 'All supervisors',
      value: filters.supervisorId ?? '',
      options: Array.from(new Set(roster.flatMap((row) => (row.supervisorId ? [row.supervisorId] : [])))).map((id) => ({
        value: id,
        label: supervisorLabel(id, lookups),
      })),
      onChange: (value) => setFilters((current) => ({ ...current, supervisorId: value || undefined })),
    },
    {
      id: 'shift',
      label: 'Shift',
      allLabel: 'All shifts',
      value: filters.shiftName ?? '',
      options: Array.from(new Set(roster.map((row) => row.shift))).sort().map((name) => ({ value: name, label: name })),
      onChange: (value) => setFilters((current) => ({ ...current, shiftName: value || undefined })),
    },
    {
      id: 'status',
      label: 'Status',
      allLabel: 'All statuses',
      value: filters.status ?? '',
      options: (Object.keys(ROSTER_STATUS_LABEL) as RosterStatus[]).map((status) => ({ value: status, label: ROSTER_STATUS_LABEL[status] })),
      onChange: (value) => setFilters((current) => ({ ...current, status: (value || undefined) as RosterStatus | undefined })),
    },
  ]

  return (
    <div className="flex flex-col gap-5">
      <section aria-label="Totals" className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <Tile label="Employees" value={filtered.length} />
        <Tile label="Active" value={filtered.filter((row) => row.status === 'active').length} />
        <Tile label="Detailed" value={filtered.filter((row) => row.status === 'detailed').length} />
      </section>

      <ReportControls
        filters={reportFilters}
        search={{ value: filters.search ?? '', onChange: (value) => setFilters((current) => ({ ...current, search: value || undefined })) }}
        onReset={() => setFilters({})}
      />

      <ExportBar
        count={`${rows.length} ${rows.length === 1 ? 'employee' : 'employees'}`}
        loading={loading}
        disabled={loading || failed || rows.length === 0}
        fileStem={`muster-seniority-roster-${todayIso()}`}
        table={() => rosterExportTable(rows, { filters, lookups, asOf: todayIso(), generatedAt: new Date() })}
      />

      {failed ? (
        <p role="alert" className="rounded-card border border-line bg-canvas p-4 text-small text-error">
          The seniority roster could not be loaded. Reload the page, or try again in a moment.
        </p>
      ) : (
        <ReportTable
          label="Seniority roster"
          caption="Employees in seniority order, with team, title, shift, qualifications, and status"
          columns={COLUMNS}
          rows={rows}
          rowKey={(row) => row.employeeId}
          sort={sort}
          onSort={(column) => setSort((current) => nextSort(current, column))}
          loading={loading}
          empty="No employees match. Clear a filter to see the whole roster."
          minWidth={1100}
        />
      )}
    </div>
  )
}
